import { Button, Group, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { getListProductsQueryKey, useCreateProduct, useListProducts } from "../api/generated/endpoints/teams/teams";
import type { FirstAdPick, Product } from "../api/generated/model";
import { ProductForm } from "../features/brands/ProductForm";
import { productDraft } from "../features/brands/product-draft";
import { ErrorAlert } from "../platform/ErrorAlert";
import type { FirstAdDestination } from "../platform/first-ad";
import { ThumbImage } from "../ui/ThumbImage";

const packshot = (product: Product) => product.images.find((item) => item.tag === "product_shot")
  ?? product.images.find((item) => item.is_main);

export function FirstAdProduct({ destination, link, pending, onPick }: {
  destination: FirstAdDestination; link: string; pending: boolean;
  onPick: (product: FirstAdPick["product"]) => Promise<unknown>;
}) {
  const { teamId, brandId } = destination;
  const [manual, setManual] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const client = useQueryClient();
  const products = useListProducts(teamId, brandId);
  const pick = async (product: Product) => {
    const image = packshot(product);
    if (!image) return;
    try { await onPick({ kind: "product", product_id: product.id, image_id: image.asset_id }); }
    catch (failure) { setError(failure); }
  };
  const create = useCreateProduct({ mutation: { onSuccess: async (product) => {
    await client.invalidateQueries({ queryKey: getListProductsQueryKey(teamId, brandId) });
    await pick(product);
  } } });
  return <Stack aria-label="Product intake">
    {products.error || error ? <ErrorAlert error={error ?? products.error} /> : null}
    {!manual && products.data?.map((product) => {
      const image = packshot(product);
      return <Group key={product.id} wrap="nowrap">
        {image?.url ? <ThumbImage full={image.url} thumb={image.thumb_url} alt="" width={64} height={64} /> : null}
        <Text>{product.name}</Text><Button variant="default" disabled={pending || !image} onClick={() => void pick(product)}>Use {product.name}</Button>
      </Group>;
    })}
    <Button variant="subtle" onClick={() => setManual(!manual)}>{manual ? "Choose a saved product" : "Add your product"}</Button>
    {manual || products.data?.length === 0 ? <ProductForm initial={productDraft()} initialStoreUrl={link}
      editing={false} pending={pending || create.isPending} error={create.error}
      library={{ teamId, brandId }} onSubmit={(data) => create.mutate({ teamId, brandId, data })} /> : null}
  </Stack>;
}
